import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslationService } from './translation.service';
import { ToastType, ToasterNotification } from '@shared/models/notifications/notifications.models';

/**
 * Single entry point for user-facing feedback. Backed by MatSnackBar so every
 * toast picks up the Material theme; features must never call MatSnackBar directly.
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly translationService = inject(TranslationService);

  public setNotification(notification: ToasterNotification): void {
    const { type, title, message } = notification.payload;
    const text = [title, message].filter(Boolean).join(' — ');
    this.open(text, type);
  }

  /** Convenience helpers — `key` is a translation key, not literal text. */
  public success(key: string): void {
    this.open(this.translationService.instant(key), ToastType.success);
  }

  public info(key: string): void {
    this.open(this.translationService.instant(key), ToastType.info);
  }

  public warning(key: string): void {
    this.open(this.translationService.instant(key), ToastType.warning);
  }

  public error(key: string): void {
    this.open(this.translationService.instant(key), ToastType.error);
  }

  private open(text: string, type: ToastType): void {
    if (!text) {
      return;
    }
    const config: MatSnackBarConfig = {
      duration: type === ToastType.error ? 8000 : 4000,
      horizontalPosition: 'end',
      verticalPosition: 'bottom',
      panelClass: [`skiss-snackbar`, `skiss-snackbar--${type}`],
    };
    this.snackBar.open(text, this.translationService.instant('COMMON.CLOSE'), config);
  }
}
